import {
  Box,
  Checkbox,
  CollapsibleCard,
  Stack,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  BasicModal,
  BasicTable,
  MenuItemType,
  Notification,
  ToastType,
} from '@components';
import { Column } from '@components/BasicTable/BasicTable';
import { DatePicker } from '@components/DateTimePickers/DatePicker';
import { PopoverCell } from '@components/Grid/components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal, openModal } from '@store/modals';
import {
  GeocodeModel,
  fetchStopsForStopsModal,
  getGoogleGeocode,
  orderStore,
  setLoadSureStore,
  setOrderStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { appointmentSchedulingStatusOptions } from '@common/stops/constants';
import {
  PencilIcon,
  ContentCopyIcon,
  MapMarkerIcon,
  PlusCircleOutlineIcon,
  DeleteIcon,
  ArrowUpCircleOutlineIcon,
  ArrowDownCircleOutlineIcon,
} from '@atgfr8/icons';
import { ILoadEditViewModel, LoadModeTypes } from '@typeDefinitions/loadTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import {
  formatBreakdownSummary,
  getActiveBreakdowns,
} from '@utils/ltl/loadItemBreakdown';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { getActiveStopIndices, handleToast, printError } from '@utils/utils';
import { debounce, size } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import {
  Index,
  Show,
  createEffect,
  createMemo,
  createSignal,
  on,
} from 'solid-js';
import { produce } from 'solid-js/store';

import { LtlQuotingModal } from '../../ltlQuotingModal';
import {
  isLoadSureVendorAttached,
  loadId,
} from '../loads/LoadInfo/LoadSureHelpers';
import { createShowAuto214, validateStopTimeChange } from './StopHelpers';
import {
  StopWindowFields,
  StopWindowSignals,
  stopWindowTimeFromStore,
} from './StopWindowFields';
import StopStreetViewMap from './StopStreetViewMap/StopStreetViewMap';
import StopsMileage from './StopsMileage';
import StopsTracking from './StopsTracking';
import { TimeRangeData } from './TimeRangeData';
import StopEditor from './stopsEditor/StopEditor';

const iconStyles = {
  color: '#0070A2',
  cursor: 'pointer',
};

type Props = {
  load: ILoadEditViewModel;
  tabIndex: number;
};

export const Stops = (props: Props) => {
  const hasCarrier = (): boolean => Boolean(props.load.carrierId);

  const [lat, setLat] = createSignal<number>();
  const [lng, setLng] = createSignal<number>();
  const [activeStop, setActiveStop] = createSignal<LastUsedTopStopModel | null>(
    null,
  );
  const [activeView, setActiveView] = createSignal('stops-main');

  type RowScratch = {
    driverInDate?: string;
    driverInTime?: string;
    driverOutDate?: string;
    driverOutTime?: string;
    driverInError?: string;
    driverOutError?: string;
  };
  type TabScratch = Partial<Record<number, RowScratch>>;
  const [rowScratch, setRowScratch] = createSignal<
    Partial<Record<number, TabScratch>>
  >({});
  // Reactive accessor for THIS tab's row map.
  const tabScratch = (): TabScratch => rowScratch()[props.tabIndex] ?? {};

  const stopWindowSignalsById = new Map<number, StopWindowSignals>();

  const getStopWindowSignals = (
    index: number,
    rowData: LastUsedTopStopModel,
  ): StopWindowSignals => {
    if (!stopWindowSignalsById.has(rowData.id)) {
      const initial = tabScratch()[index] ?? {};
      stopWindowSignalsById.set(rowData.id, {
        timeIn: createSignal(
          initial.driverInTime ?? stopWindowTimeFromStore(rowData.driverInTime),
        ),
        timeOut: createSignal(
          initial.driverOutTime ??
            stopWindowTimeFromStore(rowData.driverOutTime),
        ),
        dateIn: createSignal(
          initial.driverInDate ?? rowData.driverInTime?.split('T')[0] ?? '',
        ),
        dateOut: createSignal(
          initial.driverOutDate ?? rowData.driverOutTime?.split('T')[0] ?? '',
        ),
      });
    }
    return stopWindowSignalsById.get(rowData.id)!;
  };

  const updateRowScratch = (index: number, patch: Partial<RowScratch>) => {
    setRowScratch((prev) => {
      const tabKey = props.tabIndex;
      const tabMap: TabScratch = { ...(prev[tabKey] ?? {}) };
      const merged: RowScratch = { ...(tabMap[index] ?? {}), ...patch };
      // Drop keys explicitly set to undefined so the row entry can shrink/empty.
      (Object.keys(patch) as Array<keyof RowScratch>).forEach((k) => {
        if (patch[k] === undefined) delete merged[k];
      });
      if (Object.keys(merged).length === 0) {
        delete tabMap[index];
      } else {
        tabMap[index] = merged;
      }
      const next = { ...prev };
      if (Object.keys(tabMap).length === 0) {
        delete next[tabKey];
      } else {
        next[tabKey] = tabMap;
      }
      return next;
    });
  };

  const revalidateAllStops = () => {
    const loadIndex = props.tabIndex;
    const stops = orderStore.order.loads[loadIndex]?.stops ?? [];
    const activeIndices: number[] = [];
    stops.forEach((s, i) => {
      if (s.operationType !== 'Delete') activeIndices.push(i);
    });
    setRowScratch((prev) => {
      const tabKey = props.tabIndex;
      const oldTabMap = prev[tabKey] ?? {};
      const newTabMap: TabScratch = {};
      stops.forEach((stop, index) => {
        const position = activeIndices.indexOf(index);
        if (position < 0) return;
        const existing = oldTabMap[index] ?? {};
        // Always clear store-derived errors first.
        const row: RowScratch = {
          driverInDate: existing.driverInDate,
          driverInTime: existing.driverInTime,
          driverOutDate: existing.driverOutDate,
          driverOutTime: existing.driverOutTime,
        };

        const inDate =
          existing.driverInDate ?? stop.driverInTime?.split('T')[0] ?? '';
        const inTime =
          existing.driverInTime ?? stop.driverInTime?.split('T')[1] ?? '';
        const outDate =
          existing.driverOutDate ?? stop.driverOutTime?.split('T')[0] ?? '';
        const outTime =
          existing.driverOutTime ?? stop.driverOutTime?.split('T')[1] ?? '';
        const inIso = inDate && inTime ? `${inDate}T${inTime}` : null;
        const outIso = outDate && outTime ? `${outDate}T${outTime}` : null;
        if (inIso) {
          const r = validateStopTimeChange(
            position,
            'driverInTime',
            inIso,
            outIso,
          );
          if (!r.ok) row.driverInError = r.message;
        }
        if (outIso) {
          const r = validateStopTimeChange(
            position,
            'driverOutTime',
            outIso,
            inIso,
          );
          if (!r.ok) row.driverOutError = r.message;
        }

        (Object.keys(row) as Array<keyof RowScratch>).forEach((k) => {
          if (row[k] === undefined) delete row[k];
        });
        if (Object.keys(row).length > 0) newTabMap[index] = row;
      });
      const next = { ...prev };
      if (Object.keys(newTabMap).length === 0) {
        delete next[tabKey];
      } else {
        next[tabKey] = newTabMap;
      }
      return next;
    });
  };

  const modalId = 'stops-modal';
  const modalMapId = `map-modal-${props.tabIndex}`;
  const titles = {
    'stops-main': 'Create a Stop',
    'ltl-product-catalog': 'Product Catalog',
    'select-from-addresses': 'Add/Edit Address',
  };

  const deleteRow = (rowId: number) => {
    const stops = cloneDeep([
      ...(orderStore.order.loads[props.tabIndex].stops ?? []),
    ]);
    const stopIndex = stops.findIndex((stop) => stop.id === rowId);
    let updatedItems = cloneDeep([
      ...(orderStore.order.loads[props.tabIndex].items ?? []),
    ]);
    updatedItems = updatedItems
      .map((itm) => {
        if (
          itm.pickUpId === stops[stopIndex].id ||
          itm.dropOffId === stops[stopIndex].id
        ) {
          if (itm.operationType !== 'Insert') {
            itm.operationType = 'Delete';
          } else {
            return null;
          }
        }
        return itm;
      })
      .filter((itm) => itm !== null);
    if (stops[stopIndex].operationType == 'Insert') {
      stops.splice(stopIndex, 1);
    } else {
      stops[stopIndex] = {
        ...stops[stopIndex],
        operationType: 'Delete',
      };
    }
    updateLoadPropertyAtIndex({ stops: stops, items: updatedItems });
  };

  const handleDateChange = (value: string, index: number) => {
    setOrderStore(
      'order',
      'loads',
      props.tabIndex,
      'stops',
      index,
      produce((stop) => {
        stop.operationType =
          stop.operationType === 'None' ? 'Update' : stop.operationType;
        stop.stopDateTime = value;
      }),
    );
  };

  const handleDriverTimeChange = (
    field: 'driverInTime' | 'driverOutTime',
    value: string | null,
    index: number,
    typedDate?: string,
    typedTime?: string,
  ): { ok: true } | { ok: false; message: string } => {
    // Use props.tabIndex consistently so writes always target THIS load,
    // avoiding issues when activeTab.index might be stale or out of sync.
    const loadIndex = props.tabIndex;
    const stops = orderStore.order.loads[loadIndex]?.stops ?? [];
    const activeIndices: number[] = [];
    stops.forEach((s, i) => {
      if (s.operationType !== 'Delete') activeIndices.push(i);
    });
    const position = activeIndices.indexOf(index);
    const stop = stops[index];
    const scratch = tabScratch()[index];
    const partnerFromScratch = (() => {
      if (field === 'driverInTime') {
        const d = scratch?.driverOutDate;
        const t = scratch?.driverOutTime;
        return d && t ? `${d}T${t}` : null;
      }
      const d = scratch?.driverInDate;
      const t = scratch?.driverInTime;
      return d && t ? `${d}T${t}` : null;
    })();
    const partner =
      partnerFromScratch ??
      (field === 'driverInTime' ? stop.driverOutTime : stop.driverInTime);
    const result = validateStopTimeChange(position, field, value, partner);
    if (!result.ok) {
      // Persist BOTH the rejected value pieces AND the message so they survive
      // a row remount (e.g. after drag-drop clones the stop objects).
      if (field === 'driverInTime') {
        updateRowScratch(index, {
          driverInDate: typedDate,
          driverInTime: typedTime,
          driverInError: result.message,
        });
      } else {
        updateRowScratch(index, {
          driverOutDate: typedDate,
          driverOutTime: typedTime,
          driverOutError: result.message,
        });
      }
      // Still write the invalid value to the store so that save-time validation
      // (collectStopChronologyViolations) sees it and blocks the save. Without
      // this, the save would proceed with stale/missing data while the user
      // sees an inline error they expect to block the save.
      setOrderStore(
        'order',
        'loads',
        loadIndex,
        'stops',
        index,
        produce((stop) => {
          stop.operationType =
            stop.operationType === 'None' ? 'Update' : stop.operationType;
          stop[field] = value;
        }),
      );
      revalidateAllStops();
      return result;
    }
    if (field === 'driverInTime') {
      updateRowScratch(index, {
        driverInDate: undefined,
        driverInTime: undefined,
        driverInError: undefined,
      });
    } else {
      updateRowScratch(index, {
        driverOutDate: undefined,
        driverOutTime: undefined,
        driverOutError: undefined,
      });
    }
    setOrderStore(
      'order',
      'loads',
      loadIndex,
      'stops',
      index,
      produce((stop) => {
        stop.operationType =
          stop.operationType === 'None' ? 'Update' : stop.operationType;
        stop[field] = value;
      }),
    );
    if (value) {
      const updates = createShowAuto214(index);
      setOrderStore(
        'order',
        'loads',
        loadIndex,
        'stops',
        index,
        produce((stop) => {
          stop.ediUpdates = updates;
        }),
      );
    }
    // A change to one stop can fix or break neighbor chronology, so re-derive
    // the whole tab. Runs after setOrderStore so it sees the committed value.
    revalidateAllStops();
    return { ok: true };
  };

  const isLtlMode = () =>
    orderStore.order.loads[props.tabIndex]?.mode === 'LTL';
  const isTlMode = () => orderStore.order.loads[props.tabIndex]?.mode === 'TL';
  const facilitiesTimeRangeEnabled = () =>
    isTlMode() &&
    isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)();
  const showStopTimeRange = () =>
    (isFeatureFlagEnabled(FeatureFlags.MilitaryTime)() ||
      facilitiesTimeRangeEnabled()) &&
    !(isLtlMode() && isFeatureFlagEnabled(FeatureFlags.ltlStopModalCleanup)());
  const showLegacyStopTime = (rowData: LastUsedTopStopModel) =>
    (!isFeatureFlagEnabled(FeatureFlags.MilitaryTime)() &&
      !facilitiesTimeRangeEnabled()) ||
    (Boolean(rowData.time) &&
      !rowData.timezone &&
      rowData.beginTimeRange == null &&
      rowData.endTimeRange == null);
  const isMixedCommodityOnLoadEnabled = isFeatureFlagEnabled(
    FeatureFlags.LtlSupportMixedCommodityPalletOnLoad,
  );

  const getGeoCoordinates = async (stop: LastUsedTopStopModel) => {
    const geocode: GeocodeModel = {
      address: stop.address1 as string,
      city: stop.city as string,
      state: stop.state as string,
    };

    try {
      const response = await getGoogleGeocode(geocode);
      setLat(response.lat);
      setLng(response.lng);
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  };

  const showTotalDistance = (index: number) => {
    const activeStopIndices = getActiveStopIndices();
    if (
      Boolean(
        orderStore.order.loads[orderStore.activeTab.index].practicalMileage,
      ) &&
      size(activeStopIndices) > 1 &&
      index === activeStopIndices[size(activeStopIndices) - 1]
    ) {
      return true;
    }
    return false;
  };

  const showDistance = (rowData: LastUsedTopStopModel, index: number) => {
    const activeStopIndices = getActiveStopIndices();
    if (
      rowData.operationType !== 'Delete' &&
      Boolean(rowData.practicalMileageToNextStop) &&
      size(activeStopIndices) > 2 &&
      index < activeStopIndices[activeStopIndices.length - 1]
    ) {
      return true;
    }
    return false;
  };

  const copyAddressToClipboard = async (rowData: LastUsedTopStopModel) => {
    const address = `${rowData.address1 ?? ''} ${rowData.address2 ?? ''} ${
      rowData.city ?? ''
    } ${rowData.state ?? ''} ${rowData.zip}`;
    await navigator.clipboard.writeText(address);
    handleToast(ToastType.Success, 'Address copied to clipboard');
  };

  const columns: Column<LastUsedTopStopModel>[] = [
    {
      name: 'Date',
      key: 'date',
      renderCell: (rowData, index) => {
        return (
          <Box class="flex flex-col relative top-[8px] high-res:flex-row high-res:items-center gap-1">
            <span class="mr-1">
              {rowData.pickUp && (
                <ArrowUpCircleOutlineIcon style={iconStyles} />
              )}
              {rowData.dropOff && (
                <ArrowDownCircleOutlineIcon style={iconStyles} />
              )}
            </span>
            <DatePicker
              name="stopDateTime"
              class="[&>.MuiFormControl-root]:p-0! max-w-[140px]"
              value={rowData.stopDateTime?.split('T')[0] ?? ''}
              handleChange={(value: string) => handleDateChange(value, index)}
              disabled={orderStore.isReadOnly}
              dataTestId={`stop-date-${index}-stopDateTime`}
            />
            {/* Concat the begin, end times, and the time comments here and in the time column */}
            <Show when={isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}>
              <TimeRangeData stop={rowData} class="high-res:hidden text-sm" />
            </Show>
            <Show when={!isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}>
              <div class="high-res:hidden text-sm">{rowData.time}</div>
            </Show>
            <Show
              when={
                !isLtlMode() &&
                isFeatureFlagEnabled(
                  FeatureFlags.rls_ui_show_stop_window_non_ltl,
                )()
              }
            >
              <div class="high-res:hidden! small:table-cell!">
                <StopWindowFields
                  index={index}
                  rowData={rowData}
                  signals={getStopWindowSignals(index, rowData)}
                  scratch={() => tabScratch()[index]}
                  onDriverTimeChange={handleDriverTimeChange}
                  hasCarrier={hasCarrier()}
                />
              </div>
            </Show>
          </Box>
        );
      },
    },
    {
      name: 'Address',
      key: 'location',
      renderCell: (rowData, index) => {
        return (
          <Stack>
            <div class="font-semibold text-sm">{rowData.locationName}</div>
            <Box display="flex" gap={1} width="100%" pt={1}>
              <Box>
                <div class="text-sm">{rowData.address1}</div>
                <div class="text-sm">{rowData.address2}</div>
                <div class="text-sm">
                  {rowData.city}, {rowData.state} {rowData.zip}
                </div>
              </Box>
              <Tooltip
                title="Copy Address"
                placement="top"
                dataTestId="copy-stop-address-tooltip"
              >
                <ContentCopyIcon
                  class="text-[#0070A2] cursor-pointer size-5!"
                  onClick={() => void copyAddressToClipboard(rowData)}
                />
              </Tooltip>
            </Box>
            <div class="text-sm">
              {rowData.contact}
              <br />
              {formatPhoneNumber(rowData.phone ?? '')}
            </div>
            {showTotalDistance(index) && (
              <div class=" high-res:flex">
                <div class="text-sm font-semibold whitespace-nowrap">
                  Total Distance: &nbsp;
                </div>
                <div class="text-sm whitespace-nowrap">
                  {` ${
                    orderStore.order.loads[orderStore.activeTab.index]
                      .practicalMileage
                  } Miles (P)`}
                </div>
              </div>
            )}
            {showDistance(rowData, index) && (
              <Box style={{ display: 'flex' }}>
                <Typography
                  component="p"
                  sxProps={{ fontWeight: 'bold' }}
                  variant="inherit"
                >
                  Distance: &nbsp;
                </Typography>
                <Typography
                  variant="inherit"
                  sxProps={{ whiteSpace: 'nowrap' }}
                >
                  {` ${rowData.practicalMileageToNextStop?.toFixed()} Miles (P)`}
                </Typography>
              </Box>
            )}
          </Stack>
        );
      },
    },
    {
      name: 'Map',
      key: 'map',
      renderCell: (rowData) => (
        <MapMarkerIcon
          style={iconStyles}
          onClick={async () => {
            await getGeoCoordinates(rowData);
            openModal(modalMapId);
          }}
        />
      ),
    },
    {
      name: 'Time',
      key: 'time',
      minWidth: isLtlMode() ? 120 : 220,
      headerClasses: 'hidden! high-res:table-cell!',
      renderCell: (rowData, index) => {
        return (
          <div class="flex flex-col gap-1">
            <Show
              when={
                facilitiesTimeRangeEnabled() && rowData.facilityAppointmentType
              }
            >
              <div class="text-sm font-semibold">
                {rowData.facilityAppointmentType}
              </div>
            </Show>
            <Show
              when={
                !isLtlMode() &&
                isFeatureFlagEnabled(
                  FeatureFlags.AddressBookFacilityDetails,
                )() &&
                rowData.appointmentSchedulingStatus
              }
            >
              <div class="text-sm font-semibold">
                {appointmentSchedulingStatusOptions.find(
                  (option) =>
                    option.value === rowData.appointmentSchedulingStatus,
                )?.label ?? rowData.appointmentSchedulingStatus}
                <Show when={rowData.aiSchedulingEnabled}>
                  <span class="ml-2 text-xs font-normal">AI Schedule</span>
                </Show>
              </div>
            </Show>
            <Show when={showStopTimeRange()}>
              <TimeRangeData stop={rowData} class="text-sm" />
            </Show>
            <Show when={showLegacyStopTime(rowData)}>
              <div class="text-sm">{rowData.time ?? ''}</div>
            </Show>
            <Show
              when={
                !isLtlMode() &&
                isFeatureFlagEnabled(
                  FeatureFlags.rls_ui_show_stop_window_non_ltl,
                )()
              }
            >
              <StopWindowFields
                index={index}
                rowData={rowData}
                signals={getStopWindowSignals(index, rowData)}
                scratch={() => tabScratch()[index]}
                onDriverTimeChange={handleDriverTimeChange}
                datePickerClass="[&>.MuiFormControl-root]:p-0!"
                testIdPrefix="stop-time"
                hasCarrier={hasCarrier()}
              />
            </Show>
          </div>
        );
      },
    },
    {
      name: 'Pick/Drop #',
      key: 'pickDrop',
      renderCell: (rowData) => (
        <PopoverCell
          id={rowData.id}
          value={rowData.referenceNum}
          width={'100px'}
        />
      ),
    },
    {
      name: 'Items',
      key: 'items',
      minWidth: 130,
      renderCell: (rowData) => {
        return (
          <Index
            each={orderStore.order.loads[orderStore.activeTab.index].items}
            fallback={<></>}
          >
            {(itm) => {
              return (
                <Show
                  when={
                    itm().operationType !== 'Delete' &&
                    (itm().pickUpId == rowData.id ||
                      itm().dropOffId == rowData.id)
                  }
                  fallback={<></>}
                >
                  <div class="text-sm">{`${itm().quantity} ${itm().item} (${
                    itm().description
                  })`}</div>
                  <Show when={isLtlMode() && isMixedCommodityOnLoadEnabled()}>
                    <Index each={getActiveBreakdowns(itm().loadItemBreakdowns)}>
                      {(breakdown) => (
                        <div class="text-xs text-[#666666] pl-3">
                          {formatBreakdownSummary(breakdown())}
                        </div>
                      )}
                    </Index>
                  </Show>
                </Show>
              );
            }}
          </Index>
        );
      },
    },
    {
      name: 'Stop Instructions',
      key: 'instructions',
      minWidth: 130,
      renderCell: (rowData) => (
        <div class="text-sm">
          <Show
            when={
              Boolean(rowData.description) &&
              (rowData.description?.length ?? 0) > 0
            }
          >
            <Box
              style={{
                'overflow-wrap': 'break-word',
                'white-space': 'pre-wrap',
              }}
            >
              {rowData.description}
            </Box>
          </Show>
          <Show
            when={
              Boolean(rowData.instructions) &&
              (rowData.instructions?.length ?? 0) > 0
            }
          >
            <Box
              style={{
                'overflow-wrap': 'break-word',
                'white-space': 'pre-wrap',
              }}
            >
              {rowData.instructions}
            </Box>
          </Show>
        </div>
      ),
    },
    {
      name: 'Actions',
      key: '#',
      renderCell: (rowData) => (
        <Stack class="flex" direction="row">
          <Box class="cursor-pointer">
            <PencilIcon
              onClick={() => {
                if (orderStore.isReadOnly) {
                  return;
                }
                if (isLoadSureVendorAttached()) {
                  setLoadSureStore('isDirty', (prev) => [...prev, loadId()]);
                }
                setActiveStop(rowData);
                openModal(modalId);
              }}
              style={{
                ...iconStyles,
                'font-size': '1.5rem',
                opacity: orderStore.isReadOnly ? '0.5' : undefined,
                cursor: orderStore.isReadOnly ? 'default' : undefined,
              }}
            />
          </Box>
          <Box class="cursor-pointer">
            <DeleteIcon
              onClick={() => {
                if (orderStore.isReadOnly) {
                  return;
                }
                if (isLoadSureVendorAttached()) {
                  setLoadSureStore('isDirty', (prev) => [...prev, loadId()]);
                }
                deleteRow(rowData.id);
              }}
              style={{
                ...iconStyles,
                'font-size': '1.5rem',
                color: '#B00020',
                opacity: orderStore.isReadOnly ? '0.5' : undefined,
                cursor: orderStore.isReadOnly ? 'default' : undefined,
                'margin-left': '2px',
              }}
            />
          </Box>
        </Stack>
      ),
    },
  ];

  const footerRow = [
    {
      renderCell: () => (
        <PlusCircleOutlineIcon
          onClick={() => {
            if (orderStore.isReadOnly) {
              return;
            }

            setActiveStop(null);
            openModal(modalId);
          }}
          color="#0070A2"
          style={{
            opacity: orderStore.isReadOnly ? '0.5' : undefined,
            cursor: orderStore.isReadOnly ? 'default' : 'pointer',
          }}
        />
      ),
      key: 'ideaTitle',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
  ];

  const prepareStopsData = createMemo(() => {
    let hasDropOffStop = false;
    const dropOffOptions: MenuItemType[] = [];
    const pickUpOptions: MenuItemType[] = [];
    const stopIds: { id: number; locationName: string }[] = [];
    let smallestId = 0;
    (orderStore.order.loads[orderStore.activeTab.index]?.stops ?? []).forEach(
      (stop) => {
        if (stop.operationType === 'Delete') return; // NOTE: this is to remove the deleted stops from the selection
        stopIds.push({
          id: stop.id,
          locationName: stop.locationName ?? '',
        });
        if (stop.pickUp) {
          pickUpOptions.push({
            label: stop.locationName ?? '',
            value: stop.id.toString(),
          });
        } else {
          hasDropOffStop = true;
          dropOffOptions.push({
            label: stop.locationName ?? '',
            value: stop.id.toString(),
          });
        }
        if (stop.id < smallestId) {
          smallestId = stop.id;
        }
      },
    );
    let currentNewLoadItemId = 0;
    orderStore.order.loads[orderStore.activeTab.index].items?.forEach((itm) => {
      if (itm.id < currentNewLoadItemId) {
        currentNewLoadItemId = itm.id;
      }
    });
    return {
      hasDropOffStop,
      dropOffOptions,
      pickUpOptions,
      stopIds,
      mode: orderStore.order.loads[orderStore.activeTab.index]
        ?.mode as LoadModeTypes,
      currentNewStopId: smallestId < 0 ? smallestId - 1 : -1,
      currentNewLoadItemId:
        currentNewLoadItemId < 0 ? currentNewLoadItemId - 1 : -1,
    };
  });

  const fetchStops = debounce(async () => {
    if (
      typeof orderStore.order.customerId === 'number' &&
      orderStore.order.customerId > 0
    ) {
      try {
        await fetchStopsForStopsModal(orderStore.order.customerId);
      } catch (error) {
        printError(error);
        handleToast(ToastType.Error, (error as Error).message);
      }
    }
  }, 250);

  createEffect(
    on(
      () => orderStore.order.customerId,
      () => {
        void fetchStops();
      },
    ),
  );

  const ltlQuoteId = createMemo(() => {
    if (orderStore.isCreate && orderStore.quoteId) {
      return Number(orderStore.quoteId);
    }
    return undefined;
  });

  const ltlLoadId = createMemo(() => {
    if (
      !orderStore.isCreate &&
      orderStore.order.loads[orderStore.activeTab.index].id
    ) {
      return Number(orderStore.order.loads[orderStore.activeTab.index].id);
    }
    return undefined;
  });

  return (
    <CollapsibleCard
      class="mb-2"
      startTitle="Stops"
      startTitleAction={
        <div class="mobile:ml-12 nonMobile:ml-0">
          <StopsTracking />
        </div>
      }
      endTitle={
        <Show
          when={isFeatureFlagEnabled(
            FeatureFlags.permission_toggle_load_state_machine_exclusion,
          )()}
        >
          <Box class="ml-auto">
            <Checkbox
              size="xs"
              color="info"
              label="Auto Status Enabled"
              class={`[&>span]:text-white [&>div>input]:rounded-xs ${
                orderStore.order.loads[props.tabIndex]
                  ?.useStatusStateMachine === true
                  ? ''
                  : '[&>div>input]:bg-white!'
              }`}
              checked={
                orderStore.order.loads[props.tabIndex]
                  ?.useStatusStateMachine === true
              }
              onChange={(value) =>
                updateLoadPropertyAtIndex(
                  { useStatusStateMachine: value },
                  props.tabIndex,
                )
              }
              disabled={orderStore.isReadOnly}
              dataTestId="toggle-load-state-machine-exclusion"
            />
          </Box>
        </Show>
      }
      loading={orderStore.loading}
      contentClass="px-1!"
    >
      <Show
        when={
          orderStore.isStopsEdited &&
          orderStore.order.loads[orderStore.activeTab.index].mode === 'LTL'
        }
      >
        <Notification
          sxProps="mb-6! mt-0!"
          type="warning"
          text={
            <Typography variant="body1" component="h4">
              If you made recent changes, we recommend{' '}
              <span
                class="text-[#0070A2] font-medium hover:underline cursor-pointer"
                onClick={() => {
                  openModal('ltl-quoting-modal');
                }}
              >
                requoting
              </span>{' '}
              this shipment for most accurate pricing.
            </Typography>
          }
        />
      </Show>
      <LtlQuotingModal quoteId={ltlQuoteId()} loadId={ltlLoadId()} />
      <StopsMileage
        tabIndex={props.tabIndex}
        disabled={orderStore.isReadOnly}
      />
      <BasicTable<LastUsedTopStopModel>
        columns={columns}
        columnNoWrap={true}
        rows={props.load.stops ?? []}
        footerRow={footerRow}
        footerBorder={false}
        dragDropEnable={!orderStore.isReadOnly && !orderStore.isMobile}
        onDragEnd={(updatedItems) => {
          // Build old-index -> new-index mapping by matching reference
          // identity (DragAndDropTable hands us the same row instances in a
          // new order before we clone them below).
          const priorStops =
            orderStore.order.loads[props.tabIndex]?.stops ?? [];
          const indexMap: Partial<Record<number, number>> = {};
          updatedItems.forEach((item, newIdx) => {
            const oldIdx = priorStops.indexOf(item);
            if (oldIdx >= 0) indexMap[oldIdx] = newIdx;
          });
          // Move scratch entries (typed-but-rejected values + error messages)
          // to follow their stops to the new positions, scoped to this tab.
          setRowScratch((prev) => {
            const tabKey = props.tabIndex;
            const oldTabMap = prev[tabKey] ?? {};
            const newTabMap: TabScratch = {};
            Object.entries(oldTabMap).forEach(([k, v]) => {
              if (!v) return;
              const oldIdx = Number(k);
              const newIdx = indexMap[oldIdx];
              if (newIdx !== undefined) newTabMap[newIdx] = v;
            });
            const next = { ...prev };
            if (Object.keys(newTabMap).length === 0) {
              delete next[tabKey];
            } else {
              next[tabKey] = newTabMap;
            }
            return next;
          });
          const updatedStops = updatedItems.map((item, index: number) => ({
            ...item,
            stopOrder: index,
            operationType:
              item.operationType === 'None' ? 'Update' : item.operationType,
          }));
          setOrderStore(
            'order',
            'loads',
            props.tabIndex,
            'stops',
            updatedStops,
          );
          revalidateAllStops();
        }}
        cellClasses="px-px! py-px! text-sm! align-baseline!"
      />
      <BasicModal
        id={modalId}
        width="90vw"
        maxWidth="90vw"
        modalStyles={{ background: 'white' }}
        header={activeView() === 'ltl-product-catalog'}
        footer={false}
        onClose={() => {
          if (
            activeView() === 'ltl-product-catalog' ||
            activeView() === 'select-from-addresses'
          ) {
            setActiveView('stops-main');
          } else {
            setActiveStop(null);
            closeModal(modalId);
          }
        }}
        mainContainerStyles={{ width: '90vw' }}
        title={
          titles[
            activeView() as
              | 'stops-main'
              | 'ltl-product-catalog'
              | 'select-from-addresses'
          ] || ''
        }
        noCloseOnClickOutside
      >
        <StopEditor
          stop={activeStop()}
          hasDropOffStop={prepareStopsData().hasDropOffStop}
          dropOffOptions={prepareStopsData().dropOffOptions}
          pickUpOptions={prepareStopsData().pickUpOptions}
          stopIds={prepareStopsData().stopIds}
          currentNewStopId={prepareStopsData().currentNewStopId}
          currentNewLoadItemId={prepareStopsData().currentNewLoadItemId}
          mode={prepareStopsData().mode}
          setActiveView={setActiveView}
          activeView={activeView}
        />
      </BasicModal>
      <StopStreetViewMap lat={lat()} lng={lng()} modalId={modalMapId} />
    </CollapsibleCard>
  );
};
